import React, { createContext, useCallback, useContext, useState } from 'react';
import { CheckCircle2, AlertTriangle, Info, X } from 'lucide-react';

/**
 * Centered info/success/error popup — the app-wide replacement for the
 * bottom-right sonner toast. Call `notify({ kind, title, message })` from
 * anywhere inside <NotifyProvider/>. Dismissible via X, backdrop click,
 * or Escape.
 *
 * `kind`: 'success' | 'error' | 'info' (defaults to 'success')
 */

const NotifyContext = createContext(null);

export function useNotify() {
  const ctx = useContext(NotifyContext);
  if (!ctx) throw new Error('useNotify must be used inside <NotifyProvider>');
  return ctx;
}

export default function NotifyProvider({ children }) {
  const [stack, setStack] = useState([]); // latest first

  const dismiss = useCallback((id) => {
    setStack((s) => s.filter((n) => n.id !== id));
  }, []);

  const notify = useCallback((opts) => {
    const id = Math.random().toString(36).slice(2);
    const n = {
      id,
      kind: opts.kind || 'success',
      title: opts.title || '',
      message: opts.message || '',
      actionLabel: opts.actionLabel,
      onAction: opts.onAction,
      autoClose: opts.autoClose !== false, // default: auto-close
      duration: opts.duration || 2600,
    };
    setStack((s) => [n, ...s]);
    if (n.autoClose) {
      setTimeout(() => dismiss(id), n.duration);
    }
    return id;
  }, [dismiss]);

  const value = {
    notify,
    success: (title, message, opts) => notify({ kind: 'success', title, message, ...(opts || {}) }),
    error: (title, message, opts) => notify({ kind: 'error', title, message, autoClose: false, ...(opts || {}) }),
    info: (title, message, opts) => notify({ kind: 'info', title, message, ...(opts || {}) }),
    dismiss,
  };

  return (
    <NotifyContext.Provider value={value}>
      {children}
      {stack.length > 0 && <Overlay items={stack} dismiss={dismiss} />}
    </NotifyContext.Provider>
  );
}

const KIND_STYLES = {
  success: {
    Icon: CheckCircle2,
    iconBg: '#E6F4EA',
    iconFg: '#1E7F4E',
    ring: 'ring-[#C5E7D2]/50',
  },
  error: {
    Icon: AlertTriangle,
    iconBg: '#FFEBE9',
    iconFg: '#CE3636',
    ring: 'ring-[#FF8080]/30',
  },
  info: {
    Icon: Info,
    iconBg: '#EAF2FB',
    iconFg: '#0052CC',
    ring: 'ring-[#BFD9F2]/50',
  },
};

function Overlay({ items, dismiss }) {
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && items.length) dismiss(items[0].id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, dismiss]);

  // Only the top card is visible; older ones layered behind slightly
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm"
      onClick={() => dismiss(items[0].id)}
      data-testid="notify-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-[pop_180ms_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {items.slice(0, 1).map((n) => {
          const cfg = KIND_STYLES[n.kind] || KIND_STYLES.info;
          const Icon = cfg.Icon;
          return (
            <div key={n.id} className="relative p-6 text-center">
              <button
                onClick={() => dismiss(n.id)}
                className="absolute top-3 right-3 p-1 rounded-md hover:bg-[#F0F0EF] text-[#9B9B9B]"
                aria-label="Close"
                data-testid="notify-close"
              >
                <X className="w-4 h-4" />
              </button>
              <div
                className={`mx-auto mb-4 w-12 h-12 rounded-full flex items-center justify-center ring-8 ${cfg.ring}`}
                style={{ background: cfg.iconBg }}
              >
                <Icon className="w-6 h-6" style={{ color: cfg.iconFg }} />
              </div>
              {n.title && <h2 className="text-[17px] font-semibold text-[#191919]" data-testid="notify-title">{n.title}</h2>}
              {n.message && <p className="mt-1.5 text-[14px] leading-relaxed text-[#6B6B6B] whitespace-pre-wrap" data-testid="notify-message">{n.message}</p>}
              <div className="mt-5 flex items-center justify-center gap-2">
                {n.actionLabel && (
                  <button
                    onClick={() => { n.onAction?.(); dismiss(n.id); }}
                    className="inline-flex items-center gap-1.5 h-9 px-4 bg-[#0A0A0A] text-white text-[13px] font-medium rounded-md hover:bg-black"
                    data-testid="notify-action"
                  >
                    {n.actionLabel}
                  </button>
                )}
                <button
                  onClick={() => dismiss(n.id)}
                  className="inline-flex items-center gap-1.5 h-9 px-4 text-[13px] text-[#6B6B6B] hover:bg-[#F4F4F2] rounded-md"
                  data-testid="notify-dismiss"
                >
                  {n.actionLabel ? 'Close' : 'OK'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <style>{`@keyframes pop { 0% { transform: scale(.96); opacity: 0 } 100% { transform: scale(1); opacity: 1 } }`}</style>
    </div>
  );
}
